import axios, { Axios } from 'axios'
import { Auth } from '@aws-amplify/auth'
import { isAuth } from 'featureSliced/shared/utils/isAuth'
import { CustomError } from 'featureSliced/features/Error'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import { objectToParams } from 'featureSliced/shared/utils/objectToParams'
import * as Sentry from '@sentry/react'
import { navigate } from 'featureSliced/shared/utils/navigate'
import { capturePosthogError } from 'featureSliced/shared/utils/postHog/postHog'

export const INDEXED_DB_TABLE_NAME = `${location?.host}_REQUESTS`
export const INDEXED_DB_TABLE_VERSION = 4
export const INDEXED_DB_GET_TABLE = 'GET'

const NETWORK_ERROR_MESSAGE = 'Network Error'
const REQUEST_ERROR_MESSAGE = 'Request Error'

export class axiosInstance {
  public axiosInstance: Axios

  constructor(endpointToSet: string) {
    this.axiosInstance = axios.create({
      baseURL: endpointToSet,
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  async get(
    getPath = '/',
    withToken = false,
    params = {},
    cacheFirst = false,
    ignoreNetworkError = false,
    ignoreQueryParams = false
  ): Promise<any> {
    let res: any

    const { guid, registerId, dataEnc } = handleLocalStorage([
      'guid',
      'registerId',
      'dataEnc',
    ])

    const queryParams = {
      data: dataEnc,
      company: guid,
      register: registerId,
    }

    let path = getPath

    if (!ignoreQueryParams) {
      path += `?${objectToParams({ ...queryParams, ...params })}`
    } else {
      path += `?${objectToParams(params)}`
    }

    ///
    if (!navigator.onLine) {
      res = await this.getFromCache(path)
    }
    if (navigator.onLine && !cacheFirst) {
      res = await this.networkRequest(
        'get',
        path,
        withToken,
        null,
        ignoreNetworkError
      )
    }
    if (navigator.onLine && cacheFirst) {
      res = await this.cacheFirst(path, withToken, ignoreNetworkError)
    }
    return res
  }

  async post(path = '/', payload: any, withToken = false): Promise<any> {
    let res: any
    if (navigator.onLine) {
      res = await this.networkRequest('post', path, withToken, payload)
    }
    if (!navigator.onLine) {
      // there is no cache for POST-requests
      // res = await this.getFromCache(INDEXED_DB_POSTS_TABLE, path)
      navigate('/network-error')
    }
    return res
  }

  async networkRequest(
    method: 'get' | 'post',
    path = '/',
    withToken: boolean,
    payload?: any,
    ignoreNetworkError = false
  ) {
    try {
      const jwt = await this.getJWT(withToken)
      let data: any

      if (method === 'get') {
        const result = await this.axiosInstance.get(path, {
          headers: {
            Authorization: jwt,
          },
        })
        if (result?.data?.error?.code) {
          throw new CustomError(
            result?.data?.error?.message || 'Unknown error',
            result?.data?.error?.code
          )
          // throw new Error(result?.data?.error?.message || 'Unknown error')
        }
        data = result.data.data
        this.saveToCache(path, data)
      }

      if (method === 'post') {
        const result = await this.axiosInstance.post(path, payload, {
          headers: {
            Authorization: jwt,
          },
        })
        if (result?.data?.error?.code) {
          throw new CustomError(
            result?.data?.error?.message || 'Unknown error',
            result?.data?.error?.code
          )
          // throw new Error(result?.data?.error?.message || 'Unknown error')
        }
        data = result.data.data
      }

      return data
    } catch (error: any) {
      const { guid, dataEnc } = handleLocalStorage(['guid', 'dataEnc'])
      const errorProperties = {
        path,
        method,
        payload,
        withToken,
        dataEnc,
        guid,
        statusCode: error?.response?.status,
        errorCode: error?.response?.data?.error?.code || error.code,
        errorMessage: error?.response?.data?.error?.message || error.message,
        isNetworkError: error.message === NETWORK_ERROR_MESSAGE,
      }

      // Send to Sentry
      Sentry.addBreadcrumb({
        level: 'error',
        timestamp: Date.now(),
        type: 'api_request_error',
        message: `Failed ${path} request`,
        data: errorProperties,
      })
      Sentry.captureException(error, {
        level: 'error',
        tags: errorProperties,
      })

      // Send to PostHog
      capturePosthogError(error, {
        ...errorProperties,
        event_name: 'network_error',
      })

      if (!ignoreNetworkError && error.message === NETWORK_ERROR_MESSAGE) {
        navigate('/network-error')
      } else {
        const message = error?.response?.data?.error?.message || error.message
        const code = error?.response?.data?.error?.code || error.code
        const data = error?.response?.data?.data

        throw new CustomError(message, code, data)
      }
    }
  }

  async cacheFirst(url = '/', withToken = false, ignoreNetworkError = false) {
    // iOS lockdown/private mode has no indexedDB global at all — a bare
    // reference throws ReferenceError, not a catchable open() failure
    if (typeof indexedDB === 'undefined') {
      return this.networkRequest(
        'get',
        url,
        withToken,
        null,
        ignoreNetworkError
      )
    }
    const thisNetworkRequest = this.networkRequest.bind(this)
    const promise = new Promise(function (resolve, reject) {
      const indexed = indexedDB.open(
        INDEXED_DB_TABLE_NAME,
        INDEXED_DB_TABLE_VERSION
      )
      indexed.onupgradeneeded = () => {
        indexed.result.createObjectStore(INDEXED_DB_GET_TABLE, {
          keyPath: 'request',
        })
      }
      indexed.onsuccess = () => {
        const requests = indexed.result
          .transaction(INDEXED_DB_GET_TABLE, 'readonly')
          .objectStore(INDEXED_DB_GET_TABLE)
        const cursorRequest = requests.get(url)
        cursorRequest.onsuccess = async (e: any) => {
          if (e?.target?.result?.response) {
            thisNetworkRequest('get', url, withToken)
            resolve(JSON.parse(e.target.result.response))
          } else {
            try {
              const res = await thisNetworkRequest(
                'get',
                url,
                withToken,
                null,
                ignoreNetworkError
              )
              resolve(res)
            } catch (e: any) {
              reject(e)
            }
          }
        }
        cursorRequest.onerror = async (e: any) => {
          const res = await thisNetworkRequest('get', url, withToken)
          resolve(res)
        }
      }
      indexed.onerror = async () => {
        const res = await thisNetworkRequest('get', url, withToken)
        resolve(res)
      }
    })
    const result = await promise
    return result
  }

  async getFromCache(url = '/') {
    if (typeof indexedDB === 'undefined') return null
    const promise = new Promise(function (resolve) {
      const indexed = indexedDB.open(
        INDEXED_DB_TABLE_NAME,
        INDEXED_DB_TABLE_VERSION
      )
      indexed.onupgradeneeded = () => {
        indexed.result.createObjectStore(INDEXED_DB_GET_TABLE, {
          keyPath: 'request',
        })
      }
      indexed.onsuccess = () => {
        const requests = indexed.result
          .transaction(INDEXED_DB_GET_TABLE, 'readonly')
          .objectStore(INDEXED_DB_GET_TABLE)
        const cursorRequest = requests.get(url)
        cursorRequest.onsuccess = (e: any) => {
          if (e?.target?.result?.response) {
            resolve(JSON.parse(e.target.result.response))
          } else {
            resolve(null)
          }
        }
        cursorRequest.onerror = (e: any) => {
          navigate('/network-error')
        }
      }
    })
    const result = await promise
    return result
  }

  async getJWT(withToken = false): Promise<string | 0> {
    let jwt: string | 0 = 0
    if (withToken) {
      const isUserAuth = isAuth()
      if (isUserAuth) {
        const currentSession = await Auth?.currentSession()
        const accessToken = currentSession?.getIdToken()
        jwt = accessToken?.getJwtToken()
      }
    }
    return jwt
  }

  async saveToCache(path = '/', dataToSave: any) {
    if (typeof indexedDB === 'undefined') return
    const indexed = indexedDB.open(
      INDEXED_DB_TABLE_NAME,
      INDEXED_DB_TABLE_VERSION
    )
    indexed.onupgradeneeded = () => {
      indexed.result.createObjectStore(INDEXED_DB_GET_TABLE, {
        keyPath: 'request',
      })
    }
    indexed.onsuccess = () => {
      const posts = indexed.result
        .transaction(INDEXED_DB_GET_TABLE, 'readwrite')
        .objectStore(INDEXED_DB_GET_TABLE)
      if (dataToSave) {
        posts.put({
          request: path || '/',
          response: JSON.stringify(dataToSave),
        })
      }
    }
  }
}
